import { useState } from "react";
import api, { apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, ArrowDownCircle, ArrowUpCircle, Trash2, ChefHat } from "lucide-react";
import { toast } from "sonner";

export default function Movements() {
  const { can } = useAuth();
  const { data, loading, refetch } = useData("/stock/movements");
  const { data: stockItems } = useData("/stock");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ stock_item_id: "", type: "entree", quantite: "", note: "" });

  const openNew = () => { setForm({ stock_item_id: stockItems[0]?.id || "", type: "entree", quantite: "", note: "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stock/movements", { ...form, quantite: parseFloat(form.quantite) });
      toast.success("Mouvement enregistré"); setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer ce mouvement ? Le stock de ${m.stock_nom} sera réajusté en conséquence.`)) return;
    try {
      await api.delete(`/stock/movements/${m.id}`);
      toast.success("Mouvement supprimé — stock réajusté"); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (s) => new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const uniteOf = (m) => m.unite || stockItems.find((s) => s.id === m.stock_item_id)?.unite || "";

  const cols = [
    { header: "Date", value: (r) => fmt(r.created_at) },
    { header: "Ingrédient", value: (r) => r.stock_nom },
    { header: "Type", value: (r) => (r.type === "entree" ? "Entrée" : "Sortie") },
    { header: "Quantité", value: (r) => `${r.quantite} ${uniteOf(r)}`.trim() },
    { header: "Par", value: (r) => r.user },
    { header: "Note", value: (r) => r.note || "" },
  ];

  return (
    <div>
      <PageHeader title="Entrées / Sorties" subtitle="Suivi des mouvements de stock en temps réel.">
        {can("mouvements", "export") && <ExportButtons onExcel={() => exportExcel("mouvements_stock", cols, data)} onPDF={() => exportPDF("Mouvements de stock", cols, data)} />}
        {can("mouvements", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-movement-btn"><Plus size={16} /> Nouveau mouvement</button>}
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucun mouvement enregistré." /> : (
        <div className="tm-card overflow-x-auto" data-testid="movements-table">
          <table className="tm-table">
            <thead>
              <tr><th>Date</th><th>Ingrédient</th><th>Type</th><th className="text-right">Quantité</th><th>Par</th><th>Note</th>{can("mouvements", "delete") && <th></th>}</tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(m.created_at)}</td>
                  <td className="font-semibold">{m.stock_nom}</td>
                  <td>
                    {m.type === "entree" ? (
                      <span className="tm-badge" style={{ background: "rgba(76,175,80,.15)", color: "#4CAF50" }}><ArrowDownCircle size={13} /> Entrée</span>
                    ) : (
                      <span className="tm-badge" style={{ background: "rgba(244,67,54,.15)", color: "#F44336" }}><ArrowUpCircle size={13} /> Sortie</span>
                    )}
                  </td>
                  <td className="text-right tabular">{m.quantite} <span className="text-[var(--tm-muted)]">{uniteOf(m)}</span></td>
                  <td className="text-[var(--tm-muted)]">{m.user}</td>
                  <td className="text-[var(--tm-muted)]">
                    {m.source === "production" && (
                      <span className="tm-badge mr-2" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }}><ChefHat size={12} /> Production</span>
                    )}
                    {m.note}
                  </td>
                  {can("mouvements", "delete") && (
                    <td className="text-right">
                      <button onClick={() => remove(m)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`delete-movement-${m.id}`}><Trash2 size={16} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau mouvement" testid="movement-modal">
        <form onSubmit={save}>
          <Field label="Ingrédient">
            <select className="tm-input" required value={form.stock_item_id} onChange={(e) => setForm({ ...form, stock_item_id: e.target.value })} data-testid="movement-item">
              {stockItems.map((s) => <option key={s.id} value={s.id}>{s.nom} ({s.quantite} {s.unite})</option>)}
            </select>
          </Field>
          <Field label="Type de mouvement">
            <select className="tm-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="movement-type">
              <option value="entree">Entrée (réapprovisionnement)</option>
              <option value="sortie">Sortie (utilisation)</option>
            </select>
          </Field>
          <Field label="Quantité"><input type="number" step="0.001" className="tm-input" required value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="movement-quantite" /></Field>
          <Field label="Note"><input className="tm-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="movement-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
